import React from 'react';
import { DeepMap, FieldError } from 'react-hook-form';
import AnalyticsFactory from '../../../../services/analytics-factory';
import { EventAction, EventCategory, EventKeys } from '../../../../services/ga';
import {
	ErrorArgs,
	ErrorType,
	EventArgs,
	EventType,
} from '../../../../services/ga-enums-interfaces';
import {
	sendGaErrorEvent,
	sendGaOnlineErrorEvent,
} from '../../../domains/forms/patient/enrollment-ga-events';
import { IFormInput } from '../../../domains/forms/patient/enrollment.models';

export enum UserActionType {
	focus = 'focus',
	click = 'click',
}

export type AnalyticsProps = {
	analytics: {
		eventType: EventType;
		eventLabel: EventAction;
		eventCategory: EventCategory;
		eventAction: string;
		eventArgs: EventArgs;
		userActionType: UserActionType;
		pageRequestTime?: number;
	};
	sendAnalyticsErrors: (
		errors: ErrorArgs | DeepMap<IFormInput, FieldError>,
		errorType: ErrorType,
		fieldName?: string
	) => void;
};

//TODO: this provider ga or any other provider can be added in config instead of hardcoding
const analytics = AnalyticsFactory.createAnalytics('ga');

const sendAnalytics = (
	eventType: EventType,
	eventLabel: EventAction,
	eventCategory: EventCategory,
	eventAction: string,
	eventArgs: EventArgs,
	pageRequestTime?: number
) => {
	switch (eventType) {
		case EventType.PageRequestTimeEvent:
			analytics.sendEvent({
				[EventKeys.PageRequestTime]: new Date().getTime(),
			});
			break;
		case EventType.SimpleEvent:
			analytics.sendSimpleEvent(
				eventCategory,
				eventAction,
				eventLabel,
				pageRequestTime
			);
			break;
		default:
			analytics.sendEvent(eventArgs);
	}
};
const sendAnalyticsErrors = (
	errors: ErrorArgs | DeepMap<IFormInput, FieldError>,
	errorType: ErrorType,
	fieldName?: string
): void => {
	if (errorType === ErrorType.OnlineError) {
		sendGaOnlineErrorEvent(errors, fieldName);
	} else {
		sendGaErrorEvent(errors);
	}
};

const withAnalytics =
	<T extends AnalyticsProps>(WrappedComponent: React.ComponentType<T>) =>
	({ ...props }) => {
		const onClick = () => {
			if (
				props.analytics &&
				props.analytics.userActionType === UserActionType.click
			) {
				sendAnalytics(
					props.analytics.eventType,
					props.analytics.eventLabel,
					props.analytics.eventCategory,
					props.analytics.eventAction ?? props.children,
					props.analytics.eventArgs,
					props.analytics.pageRequestTime
				);
			}
			if (props.onClick) {
				props.onClick();
			}
		};
		const onFocus = () => {
			if (
				props.analytics &&
				props.analytics.userActionType === UserActionType.focus
			) {
				sendAnalytics(
					props.analytics.eventType,
					props.analytics.eventLabel,
					props.analytics.eventCategory,
					EventAction.FieldFocus,
					props.analytics.eventArgs,
					props.analytics.pageRequestTime
				);
			}
			if (props.onFocus) {
				props.onFocus();
			}
		};
		return (
			<WrappedComponent
				{...(props as T)}
				onClick={onClick}
				onFocus={onFocus}
				sendAnalyticsErrors={sendAnalyticsErrors}
			/>
		);
	};

export default withAnalytics;
